import React, { useEffect, useMemo, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown, useIsCoarseTablet } from '@shared/hooks/useBreakpoint';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import classNames from 'classnames';

import { SearchForm } from '@shared/components/SearchForm';
import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { FilterChip } from '@shared/components/FilterChip';

import { useStyles } from './ProcedureExecutions.styles'

import { useUserUIStore, useExecutionsGridUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { messages } from 'NeoAnalytics/neoAnalytics.messages';
import { getExecutionStatusMeta } from 'NeoAnalytics/executionSteps';
import { ExecutionsList } from '../ExecutionsList';
import { ProcedureExecutionsGraph } from '../ProcedureExecutionsGraph';
import { Typography } from '@mui/material';
import { IUserStore } from 'Users/stores';
import { useNavigate, useParams } from 'react-router-dom';
import BackIcon from '@assets/left-icon.svg';
import { RoutePaths } from '@core/routes/RoutePaths';

const STATUS_LABELS: Record<string, string> = {
    completed: 'Completed',
    failed: 'Failed',
    aborted: 'Aborted',
    abandoned: 'Abandoned',
    ongoing: 'In Progress',
};

type ExecutionFilters = {
    platforms: string[];
    modes: string[];
    statuses: string[];
};

const EMPTY_FILTERS: ExecutionFilters = { platforms: [], modes: [], statuses: [] };

type ProcedureExecutionsProps = {
    standalone?: boolean;
    hideStandaloneTitle?: boolean;
    procedureNameOverride?: string | null;
}

const TIME_FRAME_OPTIONS = ['all', 7, 30, 90] as const;

type TimeFrameOption = typeof TIME_FRAME_OPTIONS[number];

const normalizeDateValue = (value: unknown): Date | null => {
    if (value == null || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number' || typeof value === 'string') {
        const parsed = new Date(value);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
};

const getWindowDates = (days: number) => {
    const to = new Date();
    const from = new Date(to);

    from.setDate(from.getDate() - days);

    return { from, to };
};

const getUtcDayNumber = (value: Date) => Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / (1000 * 60 * 60 * 24);

const resolveSelectedTimeFrame = (fromValue: unknown, toValue: unknown): TimeFrameOption => {
    const from = normalizeDateValue(fromValue);
    const to = normalizeDateValue(toValue);

    if (!from || !to) {
        return 'all';
    }

    const daysDifference = Math.abs(getUtcDayNumber(to) - getUtcDayNumber(from));

    return TIME_FRAME_OPTIONS.find(option => typeof option === 'number' && option === daysDifference) ?? 30;
};

export const ProcedureExecutions: React.FC<ProcedureExecutionsProps> = ({ standalone = false, hideStandaloneTitle = false, procedureNameOverride = null }) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    // Coarse tablets in landscape (e.g. iPad at 1024px) sit just above the
    // tablet width threshold; treat them as narrow too, matching how the page
    // shell decides its layout, so the graph collapses and filters stack.
    const isNarrowLayout = useBreakpointDown('tablet') || useIsCoarseTablet();
    const isPhoneLayout = useBreakpointDown('mobile');
    const params = useParams();
    const { id } = params;
    const navigate = useNavigate();

    const userUIStore = useUserUIStore();
    const executionsGridUIStore = useExecutionsGridUIStore()

    const [selectedUser, setSelectedUser] = useState<IUserStore | null>();
    const [hoveredExecutionId, setHoveredExecutionId] = useState<string | null>(null);
    const [localSearchText, setLocalSearchText] = useState('');
    const [activeFilters, setActiveFilters] = useState<ExecutionFilters>(EMPTY_FILTERS);
    const [pendingFilters, setPendingFilters] = useState<ExecutionFilters>(EMPTY_FILTERS);
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    // On phones the performance graph starts collapsed so the sessions list gets the viewport.
    const [graphExpanded, setGraphExpanded] = useState(false);

    useEffect(() =>{
        if (standalone) {
            return;
        }

        const loadUser = async () => {
            const result = await userUIStore.loadUser({ id: (executionsGridUIStore.analyticsActorId ? executionsGridUIStore.analyticsActorId : id )});
            if(result){
                setSelectedUser(result.data);
            }
        };

        loadUser();

    }, [executionsGridUIStore.analyticsActorId, id, standalone, userUIStore])

    const selectedProcedureName = useMemo(() => {
        if (standalone && procedureNameOverride) {
            return procedureNameOverride;
        }

        const selectedProcedure = selectedUser?.procedures.find(procedure => procedure.id == executionsGridUIStore.analyticsEntityId);

        if (selectedProcedure) {
            return selectedProcedure.name;
        }

        return executionsGridUIStore.knownProcedureOptions.find(procedure => procedure.id === executionsGridUIStore.analyticsEntityId)?.name;
    }, [executionsGridUIStore.analyticsEntityId, executionsGridUIStore.knownProcedureOptions, procedureNameOverride, selectedUser, standalone]);

    const onExecutionsSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {return;}
        setLocalSearchText(searchText);
    };

    const openFiltersModal = () => {
        setPendingFilters(activeFilters);
        setFiltersModalOpen(true);
    };

    const applyFilters = () => {
        setActiveFilters(pendingFilters);
        setFiltersModalOpen(false);
    };

    const togglePending = (field: 'platforms' | 'modes' | 'statuses', value: string) => {
        setPendingFilters(prev => {
            const list = prev[field];

            return {
                ...prev,
                [field]: list.includes(value) ? list.filter(v => v !== value) : [...list, value],
            };
        });
    };

    const removeFilter = (field: 'platforms' | 'modes' | 'statuses', value: string) => {
        setActiveFilters(prev => ({
            ...prev,
            [field]: prev[field].filter(v => v !== value),
        }));
    };

    const clearAllFilters = () => setActiveFilters(EMPTY_FILTERS);

    const anyFilterActive =
        activeFilters.platforms.length > 0 || activeFilters.modes.length > 0 || activeFilters.statuses.length > 0;

    const onTimeFrameChange = (timeFrame: TimeFrameOption) => {
        if (timeFrame === 'all') {
            executionsGridUIStore.setParams({
                page: 0,
                from: null,
                to: null,
            });

            return;
        }

        const days = timeFrame;
        const { from, to } = getWindowDates(days);

        executionsGridUIStore.setParams({
            page: 0,
            from,
            to,
        });
    };

    const closeProcedure = () =>{
        if (standalone) {
            navigate(`/${RoutePaths.procedures}`);

            return;
        }

        executionsGridUIStore.setAnalyticsEntityId(null);
    }

    const shouldShowGraphHeader = !(standalone && hideStandaloneTitle);

    return (
        <>
            <Observer>
                {() => {
                    const allExecutions = executionsGridUIStore.executions.slice();
                    const uniquePlatforms = Array.from(
                        new Set(allExecutions.map(e => e.platform).filter((p): p is string => Boolean(p)))
                    );
                    const uniqueModes = Array.from(
                        new Set(allExecutions.map(e => e.executionMode).filter((m): m is string => Boolean(m)))
                    );
                    const uniqueStatuses = Array.from(
                        new Set(allExecutions.map(e => getExecutionStatusMeta(e, theme).state))
                    );

                    return (
                        <div className={styles.mainWrapper}>
                            <div
                                className={styles.graphWrapper}
                                style={isNarrowLayout ? { flex: '0 0 auto', minHeight: 0 } : undefined}
                            >
                                {shouldShowGraphHeader && (
                                    <div className={styles.header}>
                                        <div className={styles.headerTitleGroup}>
                                            {isNarrowLayout && (
                                                <button onClick={closeProcedure} className={styles.button}>
                                                    <img src={BackIcon} />
                                                </button>
                                            )}
                                            <Typography variant="h6" fontSize={isPhoneLayout ? 17 : 20}>
                                                {selectedProcedureName}
                                            </Typography>
                                        </div>
                                    </div>
                                )}
                                {isNarrowLayout && (
                                    <button
                                        type="button"
                                        className={styles.graphToggle}
                                        onClick={() => setGraphExpanded(expanded => !expanded)}
                                    >
                                        <span>{messages['neoAnalytics.graph.toggle']}</span>
                                        <ExpandMoreOutlinedIcon
                                            className={classNames(styles.graphToggleIcon, {
                                                [styles.graphToggleIconOpen]: graphExpanded,
                                            })}
                                        />
                                    </button>
                                )}
                                {(!isNarrowLayout || graphExpanded) && (
                                    <div
                                        className={styles.graphBody}
                                        style={isNarrowLayout ? { flex: '0 0 auto', height: 340 } : undefined}
                                    >
                                        <ProcedureExecutionsGraph
                                            hoveredExecutionId={hoveredExecutionId}
                                            onHoveredExecutionIdChange={setHoveredExecutionId}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={styles.listWrapper}>
                                <div className={classNames(styles.header, { [styles.headerNarrow]: isNarrowLayout })}>
                                    <label className={styles.contentTitle}>
                                        Sessions
                                    </label>
                                    <div className={styles.filters}>
                                        <Observer>
                                            {() => {
                                                const selectedTimeFrame = resolveSelectedTimeFrame(
                                                    executionsGridUIStore.requestParams.from,
                                                    executionsGridUIStore.requestParams.to,
                                                );

                                                return (
                                                    <>
                                                        <SearchForm
                                                            initialValues={{ search: '' }}
                                                            onSearchTextChanged={onExecutionsSearchTextChanged}
                                                            className={styles.searchWrapper}
                                                        />
                                                        <div className={styles.timeWindowControls}>
                                                            <span className={styles.timeWindowLabel}>Time Frame</span>
                                                            <div className={styles.timeWindowButtons}>
                                                                {TIME_FRAME_OPTIONS.map(timeFrame => (
                                                                    <button
                                                                        key={String(timeFrame)}
                                                                        type="button"
                                                                        onClick={() => onTimeFrameChange(timeFrame)}
                                                                        className={timeFrame === selectedTimeFrame ? styles.timeWindowButtonActive : styles.timeWindowButton}
                                                                        disabled={executionsGridUIStore.status.isLoading}
                                                                    >
                                                                        {timeFrame === 'all' ? 'All time' : `${timeFrame}d`}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )
                                            }}
                                        </Observer>
                                        <span
                                            style={{ display: 'inherit' }}
                                            title={messages['neoAnalyticsPage.filter.title']}
                                        >
                                            <FilterListOutlinedIcon
                                                onClick={openFiltersModal}
                                                className={styles.filterIcon}
                                            />
                                        </span>
                                    </div>
                                </div>
                                {anyFilterActive && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', paddingBottom: 8, flex: '0 0 auto' }}>
                                        {activeFilters.platforms.map(p => (
                                            <FilterChip key={p} text={`Platform: ${p}`} onRemove={() => removeFilter('platforms', p)} />
                                        ))}
                                        {activeFilters.modes.map(m => (
                                            <FilterChip key={m} text={`Mode: ${m}`} onRemove={() => removeFilter('modes', m)} />
                                        ))}
                                        {activeFilters.statuses.map(s => (
                                            <FilterChip key={s} text={`Status: ${STATUS_LABELS[s] ?? s}`} onRemove={() => removeFilter('statuses', s)} />
                                        ))}
                                        <p
                                            onClick={clearAllFilters}
                                            style={{ cursor: 'pointer', color: theme.colors.violet_primary, fontFamily: theme.font.secondary, fontSize: 16, margin: '4px 0' }}
                                        >
                                            Clear all
                                        </p>
                                    </div>
                                )}
                                <div className={styles.tableWrapper}>
                                    <ExecutionsList hoveredExecutionId={hoveredExecutionId} showActorColumn={standalone} searchText={localSearchText} localFilters={activeFilters} />
                                </div>
                            </div>

                            <Modal
                                isOpen={filtersModalOpen}
                                onRequestClose={() => setFiltersModalOpen(false)}
                            >
                                <div style={{ padding: 24, minWidth: 300 }}>
                                    <h3 style={{ marginTop: 0, marginBottom: 16, fontFamily: theme.font.primary, color: theme.colors.text_heading }}>
                                        Filter Sessions
                                    </h3>

                                    {uniquePlatforms.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Platform
                                            </p>
                                            {uniquePlatforms.map(p => (
                                                <FormControlLabel
                                                    key={p}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{p}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.platforms.includes(p)}
                                                            onChange={() => togglePending('platforms', p)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {uniqueModes.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Execution Mode
                                            </p>
                                            {uniqueModes.map(m => (
                                                <FormControlLabel
                                                    key={m}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{m}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.modes.includes(m)}
                                                            onChange={() => togglePending('modes', m)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {uniqueStatuses.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Status
                                            </p>
                                            {uniqueStatuses.map(s => (
                                                <FormControlLabel
                                                    key={s}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{STATUS_LABELS[s] ?? s}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.statuses.includes(s)}
                                                            onChange={() => togglePending('statuses', s)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                                        <Button
                                            color="secondary"
                                            onClick={() => setFiltersModalOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={applyFilters}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            </Modal>
                        </div>
                    );
                }}
            </Observer>
        </>
    )
}